// useBarcodeScanner — the camera scans continuously while `enabled`, via the
// native BarcodeDetector (Chrome/Edge/Android WebView; Safari has none, the
// hook reports "unsupported" and the caller falls back to typing). Same
// barcode within the cooldown is ignored while it lingers in frame.

import { useEffect, useRef, useState, type RefObject } from "react";

export type CameraState = "off" | "starting" | "on" | "unsupported" | "denied";

type BarcodeDetectorLike = {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
};

const FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128"];
const COOLDOWN_MS = 2500;
const TICK_MS = 400;

export function scannerSupported(): boolean {
  return typeof window !== "undefined" && "BarcodeDetector" in window;
}

export function useBarcodeScanner(
  videoRef: RefObject<HTMLVideoElement | null>,
  onCode: (code: string) => void,
  enabled: boolean,
): CameraState {
  // "off" and "unsupported" are derived; only the camera's own progress is state.
  const [phase, setPhase] = useState<"starting" | "on" | "denied">("starting");
  const cooldown = useRef<Map<string, number>>(new Map());
  const onCodeRef = useRef(onCode);
  useEffect(() => {
    onCodeRef.current = onCode;
  }, [onCode]);

  useEffect(() => {
    if (!enabled || !scannerSupported()) return;
    let stream: MediaStream | null = null;
    let timer: number | null = null;
    let cancelled = false;

    const Detector = (
      window as unknown as { BarcodeDetector: new (opts: { formats: string[] }) => BarcodeDetectorLike }
    ).BarcodeDetector;
    const detector = new Detector({ formats: FORMATS });

    const start = async () => {
      setPhase("starting");
      let s: MediaStream;
      try {
        s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      } catch {
        if (!cancelled) setPhase("denied");
        return;
      }
      if (cancelled) {
        s.getTracks().forEach((t) => t.stop());
        return;
      }
      stream = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        void videoRef.current.play();
      }
      setPhase("on");
      timer = window.setInterval(async () => {
        const video = videoRef.current;
        if (!video || video.readyState < 2) return;
        try {
          const codes = await detector.detect(video);
          const now = Date.now();
          for (const c of codes) {
            if (!c.rawValue) continue;
            if (now - (cooldown.current.get(c.rawValue) ?? 0) < COOLDOWN_MS) continue;
            cooldown.current.set(c.rawValue, now);
            onCodeRef.current(c.rawValue);
          }
        } catch {
          // detector hiccup — next tick retries
        }
      }, TICK_MS);
    };
    void start();

    return () => {
      cancelled = true;
      if (timer != null) window.clearInterval(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [enabled, videoRef]);

  if (!enabled) return "off";
  if (!scannerSupported()) return "unsupported";
  return phase;
}
